import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { makeStyles } from "@material-ui/core/styles";
import { getFormatedDate } from "../../../../helper";
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
} from "@material-ui/core";
import { ListItemIcon, Typography } from "@material-ui/core";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  dropdownContainer: {
    position: "absolute",
    right: "1px",
    zIndex: 10,
    background: "white",
    padding: "5px",
    borderRadius: "0.1px",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const sharedFontStyle = {
  fontFamily: "Poppins, sans-serif",
};

const DPGroupedBarLineChart = ({
  activeTab,
  graphData = [],
  orderLine,
  dollarOrDoh,
  module,
}) => {
  const classes = useStyles();
  const [selectedSeries, setSelectedSeries] = useState([]);
  const [borderRadius, setBorderRadius] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  let categories = [];
  let series = [];
  let yAxisTitle = "";

  useEffect(() => {
    if (series.length > 0) {
      setSelectedSeries(series.map((s) => s.name));
    }
  }, [graphData]);

  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(2);
      } else if (screenWidth < 1500) {
        setBorderRadius(5);
      } else {
        setBorderRadius(7);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalItems = graphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const formatToKOrM = (value) => {
    if (value >= 1e6) {
      return `${(value / 1e6).toFixed(2)}M`;
    }
    if (value >= 1e3) {
      return `${(value / 1e3).toFixed(2)}K`;
    }
    return Math.floor(value);
  };

  let orderLineFilter =
    orderLine === "100% Fulfilled"
      ? [
          {
            name: "Fulfillment",
            data: graphData.map((item) => item["100_Percent_Fulfillment"] || 0),
          },
        ]
      : [
          {
            name: "Cuts",
            data: graphData.map((item) => item["100_Percent_Cuts"] || 0),
          },
        ];

  if (activeTab === "Inv.On Hand") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    series = [
      {
        name: "Inv.On Hand",
        data: graphData.map(
          (item) =>
            item?.AVG_DOH_Stock_Coverage_Days ||
            item?.avg_inventory_on_hand_dollars ||
            0,
        ),
      },
    ];
    yAxisTitle =
      dollarOrDoh !== "DOH" ? "Inv.On Hand Dollars" : "Stock Coverage Days";
  } else if (activeTab === "Potential savings") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    series = [
      {
        name: "Potential savings",
        data: graphData.map((item) => item.QuantityValue_dollars || 0),
      },
    ];
    yAxisTitle = "Savings ($)";
  } else if (activeTab === "Inv.Salvage risk") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    series = [
      {
        name: "Inv.Salvage risk",
        data: graphData.map((item) => item.QuantityValue_dollars || 0),
      },
    ];
    yAxisTitle = "Inventory Value ($)";
  } else if (activeTab === "Accuracy") {
    categories = graphData?.map(
      (item) => getFormatedDate(item.Date) || "Unknown Metric",
    );
    series = [
      {
        name: "RDP",
        type: "bar",
        data: graphData?.map((item) => item.RDP || 0),
      },
      {
        name: "Actual",
        type: "bar",
        data: graphData?.map((item) => item.Actual || 0),
      },
      {
        name: "Accuracy",
        type: "line",
        data: graphData?.map((item) => item.Accuracy || 0),
      },
    ];
    yAxisTitle = "Throughput";
  } else if (activeTab === "Redeploy Cost") {
    categories = graphData.map((item) => item.Metric_name || "Unknown Metric");
    series = [
      {
        name: "Same time previous year",
        data: graphData.map((item) => item.Last_year_SP_redeployment_cost || 0),
      },
      {
        name: "Same time previous week",
        data: graphData.map((item) => item.Last_week_SP_redeployment_cost || 0),
      },
      {
        name: "Same time previous month",
        data: graphData.map(
          (item) => item.Last_Month_SP_redeployment_cost || 0,
        ),
      },
      {
        name: "Current time",
        data: graphData.map(
          (item) => item.Current_period_redeployment_cost || 0,
        ),
      },
    ];
    yAxisTitle = "Redeployment Cost";
  } else if (activeTab === "Over Capacity") {
    categories = graphData.map((item) => item.LaneID || "Unknown Metric");
    series = [
      {
        name: "Forecast",
        data: graphData.map((item) => item.Forecast || 0),
      },
      {
        name: "Awarded",
        data: graphData.map((item) => item.Awards || 0),
      },
      {
        name: "Excess",
        data: graphData.map((item) => item.change || 0),
      },
    ];
    yAxisTitle = "TLs excess";
  } else if (activeTab === "Under Capacity") {
    categories = graphData.map((item) => item.LaneID || "Unknown Metric");
    series = [
      {
        name: "Forecast",
        data: graphData.map((item) => item.Forecast || 0),
      },
      {
        name: "Awarded",
        data: graphData.map((item) => item.Awards || 0),
      },
      {
        name: "Shortage",
        data: graphData.map((item) => item.Change || 0),
      },
    ];
    yAxisTitle = "Truck Loads";
  } else if (activeTab === "Revenue") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_Revenue || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Constrained_Revenue || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Unconstrained_Revenue || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map(
          (item) => item.Scenario_Recommendation_Revenue || 0,
        ),
      },
    ];
    yAxisTitle = "$ Million";
  } else if (activeTab === "Cost") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_Cost || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Constrained_Cost || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Unconstrained_Cost || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map((item) => item.Scenario_Recommendation_Cost || 0),
      },
    ];
    yAxisTitle = "$ Million";
  } else if (activeTab === "Storage Utilization") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_Util * 100 || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Constrained_Util * 100 || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Unconstrained_Util * 100 || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map(
          (item) => item.Scenario_Recommendation_Util * 100 || 0,
        ),
      },
    ];
    yAxisTitle = "Percentage";
  } else if (activeTab === "Outbound Dock Utilization") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_ODUtil * 100 || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Constrained_ODUtil * 100 || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Unconstrained_ODUtil * 100 || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map(
          (item) => item.Scenario_Recommendation_ODUtil * 100 || 0,
        ),
      },
    ];
    yAxisTitle = "Percentage";
  } else if (activeTab === "Stockouts") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_Stockouts || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Constrained_Stockouts || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) => item.Scenario_Capacity_Unconstrained_Stockouts || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map(
          (item) => item.Scenario_Recommendation_Stockouts || 0,
        ),
      },
    ];
    yAxisTitle = "Pallets";
  } else if (activeTab === "Stock Out Orders") {
    categories = graphData.map((item) => item?.Network || "Unknown Metric");
    series = [
      {
        name: "Orders Without OOS",
        data: graphData.map((item) => item?.No_OutOfStock || 0),
      },
      {
        name: "Orders With OOS",
        data: graphData.map((item) => item?.OutOfStock || 0),
      },
    ];
    yAxisTitle = "No. of Lines";
  } else if (activeTab === "Value") {
    categories = graphData.map((item) => item.Name || "Unknown Metric");
    series = [
      {
        name: "Ordered Revenue",
        data: graphData.map((item) => item.Ordered_revenue || 0),
      },
      {
        name: "Selected Scenario",
        data: graphData.map((item) => item.Value || 0),
      },
      {
        name: "Base Model Run",
        data: graphData.map((item) => item.baseline_Value || 0),
      },
    ];
    yAxisTitle = "$ Millions";
  } else if (activeTab === "Orders created") {
    categories = graphData.map((item) => item.Name || "Unknown Metric");
    series = [
      {
        name: "Orders created",
        data: graphData.map((item) => item.orders_created || 0),
      },
    ];
    yAxisTitle = "Orders";
  } else if (activeTab === "Excep raised") {
    categories = graphData.map((item) => item.Name || "Unknown Metric");
    series = [
      {
        name: "Exceptions Created",
        data: graphData.map((item) => item.Exceptions_created || 0),
      },
    ];
    yAxisTitle = "# Exceptions";
  } else if (activeTab === "Cancelled orders") {
    categories = graphData.map((item) => item.Name || "Unknown Metric");
    series = [
      {
        name: "Ordered Revenue",
        data: graphData.map((item) => item.Cancelled_orders || 0),
      },
    ];
    yAxisTitle = "# Cancellations";
  } else if (activeTab === "Order value") {
    categories = graphData.map((item) => item.Name || "Unknown Metric");
    series = [
      {
        name: "Ordered Revenue",
        data: graphData.map((item) => item.order_value || 0),
      },
    ];
    yAxisTitle = "# Millions";
  } else if (activeTab === "Inbound Utilization") {
    categories = graphData.map((item) => item.LocationID || "Unknown Metric");
    series = [
      {
        name: "Base - Unconstrained",
        data: graphData.map(
          (item) => item.Base_Capacity_Unconstrained_InboundDockUtil * 100 || 0,
        ),
      },
      {
        name: "Capacity Constrained",
        data: graphData.map(
          (item) =>
            item.Scenario_Capacity_Constrained_InboundDockUtil * 100 || 0,
        ),
      },
      {
        name: "Capacity Unconstrained",
        data: graphData.map(
          (item) =>
            item.Scenario_Capacity_Unconstrained_InboundDockUtil * 100 || 0,
        ),
      },
      {
        name: "Recommended",
        data: graphData.map(
          (item) => item.Scenario_Recommendation_InboundDockUtil * 100 || 0,
        ),
      },
    ];
    yAxisTitle = "Percentage";
  } else if (activeTab === "Total Freight Spend") {
    categories = graphData.map((item) => item.year_week_ly || "Unknown Metric");
    series = [
      {
        name: "Freight spend ly",
        data: graphData.map((item) => item.Freight_spend_ly || 0),
      },
      {
        name: "Freight spend cy",
        data: graphData.map((item) => item.Freight_spend_cy || 0),
      },
    ];
    yAxisTitle = "Total Freight Spend";
  } else if (activeTab === "Order-Line Fulfillment") {
    categories = graphData.map((item) => item.Network || "Unknown Metric");
    series = orderLineFilter;
    yAxisTitle = "# of order lines";
  } else if (activeTab === "Total Detention Cost") {
    categories = graphData.map((item) => item.year_week_ly || "Unknown Metric");
    series = [
      {
        name: "Detention spend ly",
        data: graphData.map((item) => item.Detention_spend_ly || 0),
      },
      {
        name: "Detention spend cy",
        data: graphData.map((item) => item.Detention_spend_cy || 0),
      },
    ];
    yAxisTitle = "Total Detention";
  } else if (activeTab === "Order Blocks") {
    categories = graphData.map(
      (item) => item.CustomerLevel4RegionDescription || "Unknown Metric",
    );
    series = [
      {
        name: "Blocks",
        data: graphData.map((item) => item.Block || 0),
      },
    ];
    yAxisTitle = "# of Orders";
  } else if (activeTab === "Exception Monitoring") {
    categories = graphData.map((item) => item.Network || "Unknown Metric");
    series = [
      {
        name: "Recommended",
        data: graphData.map((item) => item.Recommended || 0),
      },
      {
        name: "Accepted",
        data: graphData.map((item) => item.Accepted || 0),
      },
      {
        name: "Rejected",
        data: graphData.map((item) => item.Rejected || 0),
      },
    ];
    yAxisTitle = "# Open Exception-Doc";
  } else if (activeTab === "Reason Code-Quantity Cuts") {
    categories = graphData.map((item) => item?.MaterialID || "Unknown Metric");
    series = [
      {
        name: "Reason Code",
        data: graphData.map((item) => item?.Reason_Code || 0),
      },
      {
        name: "Reason Code Opportunity",
        data: graphData.map((item) => item?.Reason_Code_Opportunity || 0),
      },
    ];
    yAxisTitle = "# Open Exception-Doc";
  } else if (activeTab === "Reason Code-Late Loads") {
    categories = graphData.map((item) => item?.Business || "Unknown Metric");
    series = [
      {
        name: "Loads with Reason Codes",
        data: graphData.map((item) => item?.Late_Loads_with_Reason_Codes || 0),
      },
      {
        name: "Loads without Reason Codes",
        data: graphData.map(
          (item) => item?.Late_Loads_without_Reason_Codes || 0,
        ),
      },
    ];
    yAxisTitle = "# Open Exception-Doc";
  } else if (activeTab === "Exceptions-Sales Doc") {
    categories = graphData.map((item) => item?.MaterialID || "Unknown Metric");
    series = [
      {
        name: "Closed",
        data: graphData.map((item) => item?.Closed || 0),
      },
      {
        name: "Open",
        data: graphData.map((item) => item?.Open || 0),
      },
    ];
    yAxisTitle = "# Open Exception-Doc";
  }

  const wideColumnTabs = [
    "Under Capacity",
    "Over Capacity",
    "Cost",
    "Storage Utilization",
    "Outbound Dock Utilization",
    "Inbound Utilization",
    "Stockouts",
    "Revenue",
    "Stock Out Orders",
    "Exception Monitoring",
    "Reason Code-Quantity Cuts",
    "Reason Code-Late Loads",
    "Exceptions-Sales Doc",
  ];

  const filteredSeries = series.filter((s) => selectedSeries.includes(s.name));

  const slicedSeries = filteredSeries.map((series) => ({
    ...series,
    data: series.data.slice(startIndex, endIndex), // Slice the data for the current page
  }));

  // Slice data for current page
  const slicedCategories = categories.slice(startIndex, endIndex);

  const formatYAxisValue = (value) => {
    if (activeTab === "Inv.On Hand") {
      if (dollarOrDoh === "Dollars") {
        return `$${formatToKOrM(value)}`;
      } else {
        return `${formatToKOrM(value)}`;
      }
    }
    if (
      [
        "Outbound Dock Utilization",
        "Storage Utilization",
        "Inbound Utilization",
      ].includes(activeTab)
    ) {
      return `${formatToKOrM(value)}%`;
    }
    if (
      [
        "Over Capacity",
        "Under Capacity",
        "Stockouts",
        "Exception Monitoring",
        "Exceptions-Sales Doc",
        "Reason Code-Late Loads",
        "Reason Code-Quantity Cuts",
        "Orders created",
        "Order-Line Fulfillment",
        "Excep raised",
        "Cancelled orders",
        "Order value",
        "Total Detention Cost",
        "Stock Out Orders",
        "Order Blocks",
      ].includes(activeTab)
    ) {
      return `${formatToKOrM(value)}`;
    } else {
      return `$${formatToKOrM(value)}`;
    }
  };

  const allowedTabs = [
    "Inbound Utilization",
    "Stockouts",
    "Outbound Dock Utilization",
    "Cost",
    "Revenue",
    "Storage Utilization",
  ];

  const chartOptions = {
    chart: {
      height: 350,
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: "easeInOutQuad",
        speed: 600,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth:
          module === "NPS"
            ? "50%"
            : module === "FGS"
              ? "15%"
              : yAxisTitle === "Redeployment Cost" ||
                  wideColumnTabs.includes(activeTab)
                ? "25%"
                : "10%",
        borderRadius,
        borderRadiusApplication: "end",
      },
    },
    grid: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 1,
      curve: "straight",
    },
    colors: ["#EC6FBD", "#A4D65D", "#0067A1", "#F7D240"],
    xaxis: {
      categories: slicedCategories,
      tickAmount: slicedCategories.length,
      //   tickPlacement: "on",
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        cssClass: "force-xaxis-rotate",
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
    },
    yaxis: {
      title: {
        text: yAxisTitle,
        style: {
          ...sharedFontStyle,
          fontSize: "12px",
          fontWeight: 500,
          color: "#333",
        },
      },
      labels: {
        trim: false,
        minWidth: 60,
        formatter: formatYAxisValue,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: formatYAxisValue,
      },
      style: sharedFontStyle,
    },
    legend: {
      show: allowedTabs.includes(activeTab) ? false : true,
      position: "top",
      horizontalAlign: "right",
      fontFamily: "Poppins, sans-serif",
      markers: {
        width: 12,
        height: 12,
        radius: 10,
      },
    },
  };

  return (
    <div className={classes.chartContainer}>
      {allowedTabs.includes(activeTab) && (
        <div className={classes.dropdownContainer}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.formControl}
          >
            <InputLabel>Select Series</InputLabel>
            <Select
              multiple
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              renderValue={(selected) =>
                selected.length > 0
                  ? `${selected.length} selected`
                  : "Select Series..."
              }
              label="Select Series"
              displayEmpty
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              }}
            >
              {series.map((s, _index) => (
                <MenuItem key={s.name} value={s.name}>
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedSeries.indexOf(s.name) > -1}
                      icon={
                        <CheckBoxOutlineBlankIcon sx={{ color: "#0067A1" }} />
                      }
                      checkedIcon={<CheckBoxIcon sx={{ color: "#0067A1" }} />}
                    />
                  </ListItemIcon>
                  <Typography variant="body2">{s.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      <ReactApexChart
        options={{
          ...chartOptions,
          xaxis: {
            ...chartOptions.xaxis,
            categories: slicedCategories, // Use sliced categories
          },
        }}
        series={
          slicedSeries.length > 0
            ? slicedSeries
            : [{ name: "No Data", data: [] }]
        } // Use sliced series
        type="bar"
        height={350}
      />

      {totalPages > 1 && (
        <div className={classes.sliderContainer}>
          <div className={classes.sliderWrapper}>
            <div
              className={classes.progressTrack}
              style={{
                width: `${((currentPage + 1) / totalPages) * 100}%`,
                opacity: 0.3,
              }}
            />
            <input
              type="range"
              min="0"
              max={totalPages - 1}
              value={currentPage}
              onChange={(e) => {
                const newPage = parseInt(e.target.value, 10);
                setCurrentPage(newPage);
              }}
              className={classes.slider}
            />
          </div>
          <div className={classes.pageIndicator}>
            <span>{currentPage + 1}</span> / {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default DPGroupedBarLineChart;
